import { ArdoqId } from '@ardoq/api-types';
import { GraphItemId, RawGraphNode2 } from '../types';
import { isTruthy } from '@ardoq/common-helpers';

const rootGroupHas = (id: string, rootGroup: RawGraphNode2): boolean => {
  if (rootGroup.modelId && id === rootGroup.modelId) {
    return true;
  }
  return rootGroup.children?.some(child => rootGroupHas(id, child)) ?? false;
};

const rootGroupContains = (
  rootGroup: RawGraphNode2,
  startSetIds: Set<string>
): boolean => {
  if (rootGroup.modelId && startSetIds.has(rootGroup.modelId)) {
    return true;
  }
  return (
    rootGroup.children?.some(child => rootGroupContains(child, startSetIds)) ??
    false
  );
};

type GetRootItemsArgs = {
  startSet: ArdoqId[];
  rootGroups: RawGraphNode2[];
  componentMap: Map<GraphItemId, RawGraphNode2>;
};
/**
 * Get all the root items in the graph. This can be either a root group which
 * contains any of the start set components, or a start set component which is
 * not contained in any of the root groups.
 */
export const getRootItems = ({
  /**
   * The user selected set of components in a Phoenix search or traversal.
   */
  startSet,
  /**
   * All the root groups as a result of applying the grouping rules
   */
  rootGroups,
  /**
   * A map of all the components in the graph.
   */
  componentMap,
}: GetRootItemsArgs) => {
  const startSetIds = new Set(startSet);
  // Only take the root groups which contain at least one of the start set
  // components.
  const startSetRootItems = rootGroups.filter(rootGroup =>
    rootGroupContains(rootGroup, startSetIds)
  );
  // Add all the start set components which are not contained in any of the
  // root groups.
  startSet
    .filter(
      id => !startSetRootItems.some(rootGroup => rootGroupHas(id, rootGroup))
    )
    .forEach(id => startSetRootItems.push(componentMap.get(id)!));

  return Array.from(new Set(startSetRootItems)).filter(isTruthy);
};
